<template>
  <div class="history-input-wrapper" ref="wrapperRef">
    <el-input
      v-model="inputValue"
      :placeholder="placeholder"
      :size="size"
      :clearable="clearable"
      @focus="showHistory"
      @blur="handleBlur"
      @keyup.enter="handleEnter"
      @input="handleInput"
    >
      <template #prefix v-if="$slots.prefix">
        <slot name="prefix"></slot>
      </template>
      <template #suffix>
        <el-tooltip 
          v-if="history.length > 0" 
          content="查看历史记录" 
          placement="top"
        >
          <el-icon 
            class="history-icon" 
            @mousedown.prevent="toggleHistory"
          >
            <Clock />
          </el-icon>
        </el-tooltip>
        <el-tooltip 
          v-else 
          content="暂无历史记录" 
          placement="top"
        >
          <el-icon class="history-icon disabled">
            <Clock />
          </el-icon>
        </el-tooltip>
      </template>
    </el-input>
    
    <!-- 历史记录下拉框 -->
    <transition name="el-zoom-in-top">
      <div 
        v-if="historyVisible && history.length > 0" 
        class="history-dropdown"
      >
        <div class="history-header">
          <span>历史记录</span>
          <el-button type="danger" link size="small" @mousedown.prevent="clearHistory">
            清空
          </el-button>
        </div>
        <div class="history-list">
          <div 
            v-for="(item, index) in history" 
            :key="index"
            class="history-item"
            @mousedown.prevent="selectHistory(item)"
          >
            <el-icon class="item-icon"><Clock /></el-icon>
            <span class="item-text">{{ truncateText(item, 50) }}</span>
            <el-icon 
              class="delete-icon" 
              @mousedown.prevent.stop="deleteHistoryItem(index)"
            >
              <Close />
            </el-icon>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { Clock, Close } from '@element-plus/icons-vue'

interface Props {
  modelValue: string
  placeholder?: string
  size?: 'large' | 'default' | 'small'
  clearable?: boolean
  storageKey: string  // localStorage 的键名，用于区分不同输入框
  maxHistory?: number  // 最大历史记录数量
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  size: 'default',
  clearable: true,
  maxHistory: 10
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'enter'): void
  (e: 'select', value: string): void
}>()

const inputValue = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const wrapperRef = ref<HTMLElement>()
const historyVisible = ref(false)
const history = ref<string[]>([])

// 加载历史记录
const loadHistory = () => {
  try {
    const stored = localStorage.getItem(`input_history_${props.storageKey}`)
    if (stored) {
      history.value = JSON.parse(stored)
    }
  } catch (e) {
    console.error('Failed to load history:', e)
    history.value = []
  }
}

// 保存历史记录
const saveHistory = () => {
  try {
    localStorage.setItem(`input_history_${props.storageKey}`, JSON.stringify(history.value))
  } catch (e) {
    console.error('Failed to save history:', e)
  }
}

// 添加到历史记录
const addToHistory = (value: string) => {
  if (!value.trim()) return
  
  // 移除已存在的相同项
  const index = history.value.indexOf(value)
  if (index > -1) {
    history.value.splice(index, 1)
  }
  
  // 添加到开头
  history.value.unshift(value)
  
  // 限制数量
  if (history.value.length > props.maxHistory) {
    history.value = history.value.slice(0, props.maxHistory)
  }
  
  saveHistory()
}

// 显示历史记录
const showHistory = () => {
  if (history.value.length > 0) {
    historyVisible.value = true
  }
}

// 切换历史记录显示
const toggleHistory = () => {
  historyVisible.value = !historyVisible.value
}

// 处理失焦
const handleBlur = () => {
  // 延迟隐藏，以便点击历史记录项生效
  setTimeout(() => {
    historyVisible.value = false
  }, 200)
}

// 选择历史记录
const selectHistory = (item: string) => {
  inputValue.value = item
  historyVisible.value = false
  emit('select', item)
}

// 删除单条历史记录
const deleteHistoryItem = (index: number) => {
  history.value.splice(index, 1)
  saveHistory()
}

// 清空历史记录
const clearHistory = () => {
  history.value = []
  saveHistory()
  historyVisible.value = false
}

// 处理回车
const handleEnter = () => {
  addToHistory(inputValue.value)
  emit('enter')
}

// 处理输入
const handleInput = () => {
  // 可以在这里添加搜索建议等功能
}

// 截断文本
const truncateText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

// 公开方法供父组件调用
const saveCurrentValue = () => {
  addToHistory(inputValue.value)
}

defineExpose({
  saveCurrentValue,
  addToHistory
})

onMounted(() => {
  loadHistory()
})
</script>

<style scoped>
.history-input-wrapper {
  position: relative;
  width: 100%;
}

.history-icon {
  cursor: pointer;
  color: #409eff;
  font-size: 16px;
  transition: all 0.2s;
  padding: 4px;
  border-radius: 4px;
}

.history-icon:hover {
  color: #66b1ff;
  background: rgba(64, 158, 255, 0.1);
  transform: scale(1.1);
}

.history-icon.disabled {
  color: #c0c4cc;
  cursor: not-allowed;
}

.history-icon.disabled:hover {
  background: none;
  transform: none;
}

.history-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow: hidden;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #eee;
  font-size: 13px;
  color: #666;
}

.history-list {
  max-height: 250px;
  overflow-y: auto;
}

.history-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.2s;
}

.history-item:hover {
  background: #f5f7fa;
}

.item-icon {
  color: #909399;
  margin-right: 8px;
  flex-shrink: 0;
}

.item-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  color: #333;
}

.delete-icon {
  color: #909399;
  margin-left: 8px;
  opacity: 0;
  transition: opacity 0.2s, color 0.2s;
  flex-shrink: 0;
}

.history-item:hover .delete-icon {
  opacity: 1;
}

.delete-icon:hover {
  color: #f56c6c;
}
</style>
